import React, { useState } from "react";
import { Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useMediaQuery } from "@/hooks/use-media-query";
const counties = ["Dublin", "Cork", "Galway", "Limerick", "Waterford", "Wexford", "Wicklow", "Louth", "Kildare", "Meath", "Westmeath", "Offaly", "Laois", "Carlow", "Kilkenny", "Tipperary", "Kerry", "Clare", "Mayo", "Sligo", "Roscommon", "Longford", "Cavan", "Monaghan", "Donegal", "Leitrim"];
export default function HeroSection() {
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const navigate = useNavigate();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (query.length > 1) {
      const filtered = counties.filter(county => county.toLowerCase().includes(query.toLowerCase()));
      setSuggestions(filtered);
    } else {
      setSuggestions([]);
    }
  };
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery) {
      const matchedCounty = counties.find(county => county.toLowerCase() === searchQuery.toLowerCase());
      if (matchedCounty) {
        navigate(`/nursing-homes/${matchedCounty.toLowerCase()}`);
      } else {
        if (suggestions.length > 0) {
          navigate(`/nursing-homes/${suggestions[0].toLowerCase()}`);
        } else {
          navigate("/nursing-homes");
        }
      }
    }
  };
  const selectSuggestion = (county: string) => {
    setSearchQuery(county);
    setSuggestions([]);
    navigate(`/nursing-homes/${county.toLowerCase()}`);
  };
  return <section className="relative py-24 bg-[#f7f3eb] md:py-[40px]">
      <div className="container">
        <div className="max-w-3xl mx-auto text-center">
          <div className="mb-0 flex justify-center">
            {/* Increased image size on mobile to take up more available space */}
            <div className={`w-full ${isDesktop ? "max-w-[504px]" : "max-w-[330px]"}`}>
              <AspectRatio ratio={1}>
                <img alt="Heart" src="/lovable-uploads/e84bc991-1646-4a87-a71c-bf8ea2c08b33.png" className="h-full w-full object-contain" />
              </AspectRatio>
            </div>
          </div>

          <h1 className="hero-text mb-6">
            Need help choosing a nursing home in Ireland?
          </h1>
          <p className="text-xl text-grandpal-gray mb-8">
            Book a 1:1 consultation with a verified care expert. Get personalized guidance on Fair Deal applications, care assessments, and family support.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button 
              size="lg"
              className="bg-grandpal-green hover:bg-grandpal-green/90 text-white px-8 py-3"
              onClick={() => navigate('/consultations')}
            >
              Browse Experts
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-grandpal-green text-grandpal-green hover:bg-grandpal-green/10 px-8 py-3"
              onClick={() => navigate('/consultations')}
            >
              Book Now
            </Button>
          </div>

          <div className="relative max-w-xl mx-auto">
            <form onSubmit={handleSearchSubmit} className="relative">
              <Input type="text" placeholder="Or search for nursing homes by county" value={searchQuery} onChange={handleSearchChange} className="pr-12 py-6 text-lg shadow-md" />
              <Button type="submit" size="icon" className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-grandpal-green-dark hover:bg-grandpal-green-darkHover">
                <Search className="h-5 w-5" />
              </Button>
            </form>

            {suggestions.length > 0 && <div className="absolute z-10 w-full mt-1 bg-white rounded-md shadow-lg border border-grandpal-beige">
                <ul className="py-1">
                  {suggestions.map(county => <li key={county} className="px-4 py-2 cursor-pointer hover:bg-grandpal-beige-light transition-colors duration-150" onClick={() => selectSuggestion(county)}>
                      {county}
                    </li>)}
                </ul>
              </div>}

            <div className="mt-4 flex flex-wrap justify-center gap-2 py-[24px]">
              <span className="text-grandpal-gray">Popular:</span>
              {["Dublin", "Cork", "Galway", "Kildare"].map(county => <Button key={county} variant="outline" size="sm" className="bg-white hover:bg-grandpal-beige border-grandpal-beige" onClick={() => navigate(`/nursing-homes/${county.toLowerCase()}`)}>
                  {county}
                </Button>)}
            </div>
          </div>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 w-full h-20 bg-white" style={{
      clipPath: 'polygon(0 100%, 100% 0, 100% 100%)'
    }}></div>
    </section>;
}